import { axiosApi, objectToQuery } from "./axios";
import { getSession, signOut } from "next-auth/react"; // for client-side usage
import { getSession as serverSession } from "@/auth"; // import your NextAuth options
import { toast } from "react-toastify";
import { showToast } from "@/app/CustomToastProvider";
import { ROAVE_API_BASE_URL } from "@/config/endpoints";

const TIMEOUT = 120000; // 80 seconds
const BLOG_CONTENT_REVALIDATE_SECONDS = 60 * 30;
const STATIC_CONTENT_REVALIDATE_SECONDS = 60 * 60;
const apiBaseUrl = ROAVE_API_BASE_URL;
const OPTIONAL_AUTH_SESSION_TIMEOUT_MS = 350;

const getClientSession = async (authOptional = false) => {
  if (typeof window === "undefined") {
    return null;
  }

  if (!authOptional) {
    return getSession();
  }

  return Promise.race([
    getSession().catch(() => null),
    new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), OPTIONAL_AUTH_SESSION_TIMEOUT_MS)
    ),
  ]);
};

export const postApi = async (
  formData: any,
  onProgress?: (progress: number) => void,
  req?: any,
  res?: any,
): Promise<any> => {
  const { url, payload, apiUrl, authOptional = false } = formData;
  let session: any = null;

  // Public endpoints should not wait on NextAuth before sending the API request.
  session = await getClientSession(authOptional);

  // For server-side usage
  if (typeof window === "undefined" && req && res) {
    session = await serverSession();
  }

  const hasAuthToken = Boolean(session?.user?.auth_token);

  try {
    const config: any = {
      onUploadProgress: (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log("percentCompleted", percentCompleted);
          if (onProgress) {
            onProgress(percentCompleted); // Update progress
          }
        }
      },
      headers: {
        "Content-Type": "application/json",
        ...(session?.user?.auth_token
          ? { Authorization: `Bearer ${session?.user?.auth_token}` }
          : {}),
      },
      // Keep public endpoints accessible for guests, but preserve auth when user token exists.
      skipAuth: authOptional && !hasAuthToken,
    };
    const response = await axiosApi.post(
      `${apiUrl ?? apiBaseUrl}/${url}`,
      payload,
      config
    );
    if (response?.data?.status && response?.data?.status === 401 && !authOptional) {
      showToast("Session expired. Please login again.", "danger");
      signOut({ callbackUrl: "/login" });
    }
    if (onProgress) onProgress(100); // Ensure progress reaches 100% on success
    return response?.data;
  } catch (error: any) {
    // console.log("postApi error:", error);
    if (onProgress) onProgress(0); // Reset progress on error
    return error?.response?.data;
  }
};
export const deleteApi = async (formData: any): Promise<any> => {
  let session: any = null;

  // For client-side usage
  if (typeof window !== "undefined") {
    session = await getSession();
  }

  const { url, payload, apiUrl } = formData;
  // console.log("deleteApi payload", payload);
  try {
    const response = await axiosApi.delete(`${apiUrl ?? apiBaseUrl}/${url}`, {
      data: payload,
      headers: {
        "Content-Type": "application/json",
        ...(session?.user?.auth_token
          ? { Authorization: `Bearer ${session?.user?.auth_token}` }
          : {}),
      },
    });
    // console.log("deleteApi response:", response);
    if (response?.data?.status && response?.data?.status === 401) {
      showToast("Session expired. Please login again.", "danger");
      signOut({ callbackUrl: "/login" });
    }
    return response?.data;
  } catch (error: any) {
    console.log("deleteApi error:", error);
    return error?.response?.data;
  }
};

export const putApi = async (formData: any): Promise<any> => {
  let session: any = null;

  // For client-side usage
  if (typeof window !== "undefined") {
    session = await getSession();
  }
  const { url, payload, apiUrl } = formData;
  try {
    const response = await axiosApi.put(
      `${apiUrl ?? apiBaseUrl}/${url}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...(session?.user?.auth_token
            ? { Authorization: `Bearer ${session?.user?.auth_token}` }
            : {}),
        },
      }
    );
    console.log("putApi response:", response);
    if (response?.data?.status && response?.data?.status === 401) {
      // console.log("putApi 401",response);
      showToast("Session expired. Please login again.", "danger");
      signOut({ callbackUrl: "/login" });
    }
    return response?.data;
  } catch (error: any) {
    console.log("postApi error:", error);
    return error?.response?.data;
  }
};

export const getApi = async (formData: any): Promise<any> => {
  let queryString = "";
  const { url, payload, apiUrl } = formData;
  if (payload) {
    queryString = objectToQuery(payload);
  }
  try {
    const response = await axiosApi.get(
      `${apiUrl ?? apiBaseUrl}/${url}?${queryString}`
    );
    return response?.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

// Update to support session retrieval in getFetchApi
export const getFetchApi = async (
  formData: any,
  req?: any,
  res?: any,
  onProgress?: (progress: number) => void // Progress callback
): Promise<any> => {
  const { url, payload, apiUrl, headers = {}, authOptional = false, timeoutMs: requestTimeoutMs = TIMEOUT } = formData;
  let session: any = null;

  // Public endpoints should not wait on NextAuth before sending the API request.
  session = await getClientSession(authOptional);

  // For server-side usage
  if (typeof window === "undefined" && req && res) {
    session = await serverSession();
  }

  // Create timeout controller for fetch
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);

  let queryString = "";
  if (payload) {
    queryString = objectToQuery(payload);
  }
  // Start progress
  let progress = 1; // Start progress
  if (onProgress) onProgress(progress);

  // Simulate smooth progress updates
  const progressInterval = setInterval(() => {
    progress += Math.random() * 10; // Increment randomly (5-10)
    if (progress > 99) clearInterval(progressInterval); // Stop at 90%
    if (onProgress) onProgress(Math.min(progress, 99)); // Limit max progress
  }, 300); //

  const isExternalApi = !!apiUrl && apiUrl !== apiBaseUrl;
  const reqHeaders = {
    "Content-Type": "application/json",
    ...(!isExternalApi ? { "X-ENV": process.env.NEXT_PUBLIC_X_ENV } : {}),
    "CF-Allow": "true",
    "User-Agent": "LetsRoave-NextJS-Server/1.0",
    ...(session?.user?.auth_token
      ? { Authorization: `Bearer ${session?.user?.auth_token}` }
      : {}),
    ...headers, // Add custom headers
  }
  // console.log("getFetchApi reqHeaders", reqHeaders);
  try {
    let cacheOptions = {};
    if (url && (url === "v1/blogs" || url === "v1/blogs/categories" || url.startsWith("v1/blogs/"))) {
      cacheOptions = {
        next: { revalidate: BLOG_CONTENT_REVALIDATE_SECONDS }
      };
    }
    else if (url && url.startsWith("v1/static-content/")) {
      cacheOptions = {
        next: { revalidate: STATIC_CONTENT_REVALIDATE_SECONDS }
      };
    }
    else if (url && url === "v1/deals") {
      cacheOptions = {
        next: { revalidate: 300 }
      };
    }
    else if(url && url == "v1/blogs-sitemap"){
        cacheOptions = {
          // cache: 'no-store'
          next: { revalidate: 3600 } // Cache for 1 hour
        }
     }
     else{
        cacheOptions = {
          next: { revalidate: 10 }
        }
     }
     console.log("cacheOptions", cacheOptions)
    const res = await fetch(`${apiUrl ?? apiBaseUrl}/${url}?${queryString}`, {
      method: "GET",
      headers: reqHeaders,
      signal: controller.signal,
      // next: { revalidate: 10 }
      ...cacheOptions
    });

    clearTimeout(timeoutId);
    clearInterval(progressInterval); // Stop progress updates
    // console.log("getFetchApi res:", res);
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const result = await res.json();
    // console.log("Result", result);
    if (onProgress) onProgress(100); // Mark as complete
    if (result?.status && result?.status === 401 && !authOptional) {
      // console.log("getFetchApi 401",result);
      showToast("Session expired. Please login again.", "danger");
      signOut({ callbackUrl: "/login" });
    }
    return result;
  } catch (error: any) {
    // console.error("getFetchApi catch fired:", error);

    clearInterval(progressInterval); // Stop progress updates on error
    if (onProgress) onProgress(0); // Reset progress on failure

    if (error.name === "AbortError") {
      // showToast("Request timed out.", "danger");
      console.log("Request timed out.");
    } else {
      console.error("getFetchApi error:", error);
    }

    throw error;
  }
};


// Generic fetcher utility
export const fetcher = (url: any) => fetch(url).then((r) => r.json());
